import React, { FC, ReactNode } from 'react'
import clsx from 'clsx'

import BillsHeader from './common/BillsHeader'

import { getFloatPrice } from '../../../helpers/price'
import { getRiderHorses } from '../../../helpers/riders'

import useHorse from '../../../pages/organizer/manage/common/table/HorsesManage/hooks/useHorse'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../models/registrations-by-day/registrationByDay.interface'
import { IHorseData } from '../../../models/horse/horse.interface'

import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../types/payment'

type IBillDetailsRegistrationDataProps = {
  registrationsByDay: IRegistrationByDayInterface[]
  modalThemeColor: `#${string}`
  showRefund: boolean
  refundregistrationTotalState: number
  currency: string
}

type ITotalRowArgs = {
  modalThemeColor: `#${string}`
  item: string
  horses: string
  rider: string
  price: string | number
  showRefund?: boolean
  refundregistrationTotalState: number
  currency: string
}

type IDetailRow = {
  horses: IHorseData[]
  showRefund: boolean
  currOneDayRegistration: IRegistrationByDayInterface
  currency: string
}

const EventReviewPublishInput: FC<{
  className?: string
  data: ReactNode
  price?: string | number
  icon?: string
  postIcon?: string
  style?: React.CSSProperties
}> = ({ className, data, price, icon, postIcon, style = {} }) => {
  return (
    <div className={className} style={style}>
      {icon ? (
        <img className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2" src={icon} alt="icon" />
      ) : null}
      {price && <div className="text-SeabiscuitDark200ThemeColor font-normal mr-1">{price}</div>}
      <div className={clsx('max-w-full', icon && 'pl-6', postIcon && 'pr-6')}>{data}</div>
      {postIcon ? (
        <img
          className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
          src={postIcon}
          alt="icon"
        />
      ) : null}
    </div>
  )
}

const DetailRow = ({ horses, showRefund, currOneDayRegistration, currency }: IDetailRow) => {
  return (
    <div className="flex flex-col md:flex-row w-full mb-5 md:mb-0">
      <EventReviewPublishInput
        className="rounded-lg min-h-11 bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 ml-0 w-full md:w-4/12"
        data={
          <div className="flex flex-wrap items-center gap-1 w-full">
            <span>{currOneDayRegistration.registrationByDayName}</span>
            <div className="ml-auto text-[10px] text-SeabiscuitDark200ThemeColor flex items-center gap-2">
              {currOneDayRegistration.isScratched && (
                <span className="border border-SeabiscuitDark200ThemeColor  px-2 py-0.5 rounded-full">
                  Scratched
                </span>
              )}
              {currOneDayRegistration.isQualify && (
                <span className="border border-SeabiscuitDark200ThemeColor px-2 py-0.5 rounded-full">
                  Bonus Fee
                </span>
              )}
            </div>
          </div>
        }
      />
      <EventReviewPublishInput
        className="rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-center text-sm p-3 m-1 w-full md:w-3/12"
        data={horses[0] ? horses.map((item) => item.horseName).join(', ') : HORSE_NAME_DEFAULT}
        icon={'/assets/og_icons/YearofHorse-1.svg'}
      />
      <EventReviewPublishInput
        className="rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full md:w-3/12 capitalize"
        data={currOneDayRegistration.riderName}
        icon={'/assets/cp_icons/User-1.svg'}
      />
      <EventReviewPublishInput
        className="relative rounded-lg text-center bg-SeabiscuitGrayThemeColor flex items-center justify-center text-sm p-3 m-1 w-full md:w-2/12"
        data={
          <span>
            {getFloatPrice(`${currency}${currOneDayRegistration.registrationPrice}`)}{' '}
            {currOneDayRegistration.isQualify && (
              <span>
                • {getFloatPrice(`${currency}${currOneDayRegistration.qualifyFee}`)}{' '}
                {currOneDayRegistration.qualifyFeeName || 'qual'}
              </span>
            )}
          </span>
        }
        icon={
          currOneDayRegistration.paymentType === PAYMENT_TYPE.CARD
            ? '/assets/img/dark/credit-card.svg'
            : currOneDayRegistration.paymentType === PAYMENT_TYPE.CHECK
              ? '/assets/img/dark/check.svg'
              : '/assets/img/dark/cash.svg'
        }
        postIcon={
          currOneDayRegistration.paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/cp_icons/Ok-3.svg'
            : '/assets/cp_icons/Cancel-2.svg'
        }
      />

      {showRefund && (
        <>
          <EventReviewPublishInput
            className="flex flex-col justify-center items-center rounded-lg text-center bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 w-full md:w-3/12"
            data={`${currOneDayRegistration.amountScratched > 0 ? 'Scratched' : 'No'}`}
          />
          <EventReviewPublishInput
            className="flex flex-col justify-center items-center rounded-lg text-center bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 w-full md:w-3/12"
            data={getFloatPrice(`${currency}${currOneDayRegistration.amountRefunded}`)}
          />
        </>
      )}
    </div>
  )
}

const TotalRow = (args: ITotalRowArgs) => {
  const { item, horses, rider, price, showRefund, refundregistrationTotalState, currency } = args

  return (
    <>
      {showRefund ? (
        <>
          <div className="flex flex-col md:flex-row md:items-start w-full">
            <EventReviewPublishInput
              className={
                'rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-[21%] font-semibold bg-SeabiscuitGreenLightThemeColor'
              }
              data={`Total`}
            />
            <EventReviewPublishInput
              className="rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-10/12 font-semibold flex md:justify-end bg-SeabiscuitGreenLightThemeColor"
              data={`${getFloatPrice(`${currency}${refundregistrationTotalState}`)} refunded`}
              price={`${getFloatPrice(`${currency}${price}`)} paid,`}
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col md:flex-row md:items-start w-full">
          <EventReviewPublishInput
            className="rounded-lg text-[#122B46] text-sm p-3 mb-3 md:md-0 md:m-1 ml-0 w-full md:w-4/12 font-semibold"
            style={{
              backgroundColor: args.modalThemeColor,
            }}
            data={`Total ${item} Registration`}
          />
          <EventReviewPublishInput
            className="rounded-lg text-[#122B46] relative flex items-center justify-center text-sm p-3 mb-3 md:md-0 md:m-1 w-full md:w-3/12 font-semibold"
            style={{
              backgroundColor: args.modalThemeColor,
            }}
            data={`${horses} horse`}
          />
          <EventReviewPublishInput
            className="rounded-lg text-[#122B46] relative flex items-center justify-center text-sm p-3 mb-3 md:md-0 md:m-1 w-full md:w-3/12 font-semibold"
            style={{
              backgroundColor: args.modalThemeColor,
            }}
            data={`${rider} rider`}
          />
          <EventReviewPublishInput
            className="rounded-lg text-center text-[#122B46] text-sm p-3 md:m-1 w-full md:w-3/12 font-semibold"
            style={{
              backgroundColor: args.modalThemeColor,
            }}
            data={getFloatPrice(`${currency}${price}`)}
          />
        </div>
      )}
    </>
  )
}

const BillDetailsRegistrationData = ({
  registrationsByDay,
  showRefund,
  refundregistrationTotalState,
  modalThemeColor,
  currency,
}: IBillDetailsRegistrationDataProps) => {
  const { allHorses } = useHorse(registrationsByDay[0]?.eventId ?? '')

  // Hooks and vars
  let registerCount = new Set()
  let price = 0
  let qualifyFee = 0

  const uniqueHorses: string[] = []

  registrationsByDay.forEach((registration) => {
    registration.horsesSelected?.forEach((horse) => {
      if (!uniqueHorses.includes(horse.id)) {
        uniqueHorses.push(horse.id)
      }
    })
  })

  return (
    <>
      {registrationsByDay?.[0] ? (
        <div className="flex items-center">
          <BillsHeader title="Class" tooltipText="Registration" />
          <BillsHeader title="Horse" tooltipText="Registered horse" />
          <BillsHeader title="Rider" tooltipText="Registered rider" />
          <BillsHeader title="Price" tooltipText="Price" />
          {showRefund && (
            <>
              <BillsHeader title="Scratched" tooltipText="Scratched" />
              <BillsHeader title="Refunded" tooltipText="Refunded" />{' '}
            </>
          )}
        </div>
      ) : null}

      {registrationsByDay?.[0] ? (
        registrationsByDay?.map((currOneDayRegistration, index) => {
          registerCount.add(currOneDayRegistration.riderId)
          if (
            currOneDayRegistration.registrationPrice &&
            currOneDayRegistration.paymentStatus === PAYMENT_STATUS.PAID
          ) {
            price += currOneDayRegistration.registrationPrice
            if (currOneDayRegistration.isQualify)
              qualifyFee += Number(currOneDayRegistration.qualifyFee)
          }

          return (
            <DetailRow
              key={index}
              horses={getRiderHorses(allHorses, currOneDayRegistration?.horsesSelected || [])}
              showRefund={showRefund ?? false}
              currOneDayRegistration={currOneDayRegistration}
              currency={currency}
            />
          )
        })
      ) : (
        <span className="w-full text-sm text-SeabiscuitLightParagraphTextColor text-center">
          No fees added
        </span>
      )}

      {registrationsByDay?.length ? (
        <TotalRow
          modalThemeColor={modalThemeColor}
          item={`${registrationsByDay.length}`}
          horses={`${uniqueHorses.length}`}
          rider={`${registerCount.size}`}
          price={price + qualifyFee}
          showRefund={showRefund ?? false}
          refundregistrationTotalState={refundregistrationTotalState}
          currency={currency}
        />
      ) : null}
    </>
  )
}

export default BillDetailsRegistrationData
